import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAgents } from '@/lib/api/agents';
import { useEvalSuites } from '@/lib/api/eval-suites';

import { ReplaySuiteBuilder } from './replay-suite-builder';

const PAGE_SIZE = 20;

export function SuitesListPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const suitesQuery = useEvalSuites({
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
  });

  // Resolve the pinned agent's name client-side — the suite contract carries only
  // agentId. Mirrors the suite form's agent picker (same query, so it's cached).
  const agentsQuery = useAgents({ page: 1, pageSize: 100 });
  const agentLabels = new Map(
    (agentsQuery.data?.items ?? []).map((agent) => [
      agent.id,
      `${agent.activeVersion.name} (${agent.activeVersion.slug})`,
    ]),
  );

  const onSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const result = suitesQuery.data;
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Eval suites</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Agent-version configs and their eval tasks.
          </p>
        </div>
        <Button asChild>
          <Link to="/evals/suites/new">
            <Plus className="size-4" />
            New suite
          </Link>
        </Button>
      </div>

      <ReplaySuiteBuilder />

      <Input
        placeholder="Search by name…"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
        className="h-8 w-full sm:w-64"
      />

      {suitesQuery.isPending && <p className="text-sm">Loading…</p>}
      {suitesQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load suites: {suitesQuery.error.message}
        </p>
      )}

      {result && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.items.length ? (
                  result.items.map((suite) => (
                    <TableRow key={suite.id}>
                      <TableCell>
                        <Link
                          to="/evals/suites/$suiteId"
                          params={{ suiteId: suite.id }}
                          className="font-medium hover:underline"
                        >
                          {suite.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">
                        {suite.agentId ? (
                          (agentLabels.get(suite.agentId) ??
                          (agentsQuery.isPending ? '…' : 'Unknown agent'))
                        ) : (
                          <span className="text-[var(--color-muted-foreground)]">Unpinned</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} className="h-24 text-center">
                      No suites yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-muted-foreground)]">{result.total} suite(s)</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                Page {page} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => value + 1)}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
